import React, { useMemo } from 'react'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { VatType } from '@cbgms/api'
import { Subscriptions } from '@carsys/enums/subscription'
import { FormValues } from 'app/settings/components/LabourRates/New'
import { CurrencyInput, Form } from '@carsys/form'
import * as Yup from 'yup'
import { BaseStepLayout } from '../BaseStepLayout'
import { StepNavigation } from '../StepNavigation'
import { defaultStepFormProps } from '../types.d'
import { WithRunInBackgroundHelper } from '../use-should-run-in-background'

const nonEmptyValues = (input: object): object => {
  const entriesWithActualValues = Object.entries(input).filter(([_, value]) => !!value)
  return Object.fromEntries(entriesWithActualValues)
}
interface OnboardingLabourRateFormProps extends defaultStepFormProps {
  onSubmit: (lbr: FormValues) => void
  modules: Subscriptions[]
}

export const OnboardingLabourRateForm: React.FC<OnboardingLabourRateFormProps> = ({
  onSubmit,
  prevStep,
  isFirstStep,
  modules,
  isPersisting,
  onSubmitSuccess,
  hideSkip,
  shouldRunInBackground,
  initialData
}) => {
  const { t } = useTranslation()

  const requiredMessage = t('global.field_is_required')
  const validationSchema = Yup.object().shape({
    HourlyRate: Yup.number().required(requiredMessage).positive()
  })

  const defaultCode = t('onboarding.labour_rate.default_code')
  const defaultDescription = t('onboarding.labour_rate.default_description')

  const initialValues = useMemo(
    () =>
      ({
        VatType: VatType.High,
        Code: defaultCode,
        Description: defaultDescription,
        HourlyRate: modules.includes(Subscriptions.Module_Portal) ? 60 : 55,
        ...nonEmptyValues(initialData)
      } as FormValues),
    [defaultCode, defaultDescription]
  )

  const formSubmit = async (v: FormValues) => {
    onSubmit(v)
    return v
  }

  return (
    <BaseStepLayout
      title={t('onboarding.labour_rate.labour_rate_step_header')}
      subTitles={[t('onboarding.labour_rate.labour_rate_step_info')]}
    >
      <Form
        onSubmit={formSubmit}
        onSubmitSuccess={onSubmitSuccess}
        initialValues={initialValues}
        NavigationConfirmation={null}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, handleSubmit }) => (
          <WithRunInBackgroundHelper enabled={shouldRunInBackground} isSubmitting={isSubmitting} onSubmit={handleSubmit}>
            <Box>
              <CurrencyInput
                name='HourlyRate'
                label={t('setting_labour_rate.hourlyrate')}
                required
                data-testid='hourly-rate-input'
                fullWidth={false}
              />
            </Box>
            <StepNavigation prevStep={prevStep} hideBack={isFirstStep} isLoading={isSubmitting || isPersisting} hideSkip={hideSkip} />
          </WithRunInBackgroundHelper>
        )}
      </Form>
    </BaseStepLayout>
  )
}

export default OnboardingLabourRateForm
